import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useState } from "react";
import { useCart, useWishlist } from "@/lib/store";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const wishlist = useWishlist();
  const [hovered, setHovered] = useState(false);
  const minPrice = Math.min(...product.sizes.map((s) => s.price));
  const cheapest = product.sizes.reduce((a, b) => (a.price <= b.price ? a : b));
  const compareAt = cheapest.compare_at_price;
  const onSale = compareAt && compareAt > cheapest.price;
  const discountPct = onSale ? Math.round(((compareAt - cheapest.price) / compareAt) * 100) : 0;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, product.sizes[0].ml, 1);
    toast.success("Añadido al carrito", { description: `${product.name} — ${product.sizes[0].ml}ml` });
  };

  return (
    <Link
      to={`/producto/${product.slug}`}
      className="group block fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`product-card-${product.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative overflow-hidden bg-white aspect-square mb-4">
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-contain bg-white transition-transform duration-500 ease-out group-hover:scale-105 p-2"
        />
        {product.images[1] && (
          <img
            src={product.images[1]}
            alt=""
            aria-hidden
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-contain bg-white transition-opacity duration-500 p-2 ${hovered ? "opacity-100" : "opacity-0"}`}
          />
        )}
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {onSale && (
            <span
              className="font-mono-caps bg-[#8B0000] text-cream px-2 py-1 text-[10px]"
              data-testid={`sale-badge-${product.slug}`}
            >
              −{discountPct}%
            </span>
          )}
          {product.badges?.slice(0, 2).map((b) => (
            <span key={b} className="font-mono-caps bg-white/95 text-black px-2 py-1 text-[10px]">{b}</span>
          ))}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); wishlist.toggle(product.id); }}
          className="absolute top-3 right-3 p-2 bg-white/95 hover:bg-gold transition-colors"
          data-testid={`wishlist-btn-${product.slug}`}
          aria-label="Añadir a favoritos"
        >
          <Heart strokeWidth={1.25} className={`h-4 w-4 ${wishlist.has(product.id) ? "fill-black text-black" : "text-black"}`} />
        </button>

        {/* Quick add on hover */}
        <button
          onClick={quickAdd}
          data-testid={`quick-add-${product.slug}`}
          className={`absolute left-3 right-3 bottom-3 bg-navy text-cream py-3 font-mono-caps transition-all duration-300 ${hovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"} hover:bg-cream hover:text-navy`}
        >
          Añadir · {product.sizes[0].ml}ml
        </button>
      </div>

      <div className="space-y-1">
        <div className="font-mono-caps text-muted-foreground">{product.brand}</div>
        <h3 className="font-serif text-xl leading-tight">{product.name}</h3>
        <div className="flex items-baseline justify-between pt-1">
          <div className="text-sm text-muted-foreground">{product.concentration} · {product.family}</div>
          <div className="font-medium flex items-baseline gap-2">
            {onSale && (
              <span
                className="text-xs text-muted-foreground line-through"
                data-testid={`compare-price-${product.slug}`}
              >
                ${compareAt.toFixed(2)}
              </span>
            )}
            <span className={onSale ? "text-[#8B0000]" : ""}>Desde ${minPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
